import React, { useEffect, useState, useRef, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, MicIcon, ImageIcon, PaperclipIcon, ArrowUpIcon, CarIcon, HomeIcon, BriefcaseIcon, TagIcon, TruckIcon, SmartphoneIcon, HistoryIcon, ChevronRightIcon, ChevronUpIcon, XIcon, PlusIcon, TrendingDownIcon, ThumbsUpIcon, AlertTriangleIcon, WrenchIcon, ShoppingBagIcon, GraduationCapIcon, PlaneIcon, UserIcon, Trash2Icon } from 'lucide-react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { AIResponse } from '../components/AIResponse';
import { ListingCard, ListingCardProps } from '../components/ListingCard';
import { ListingCarousel } from '../components/ListingCarousel';
import { useBusiness, BusinessType } from '../components/BusinessContext';
// --- Seller intent detection ---
type SellIntent = {
  tier: 'individual' | 'business' | 'enterprise';
  type: BusinessType;
};
const detectSellIntent = (text: string): SellIntent | null => {
  const t = text.toLowerCase();
  const sellish = /\b(sell|selling|list|listing|post|inventory|stock|dealer|agency|recruit|hire|hiring|jobs?|shop|store|business|merchant|products?|services?|manage (leads|inquiries|properties|listings))\b/.test(t);
  if (!sellish) return null;
  // Business type
  let type: BusinessType = 'generic';
  if (/\b(car|cars|vehicle|vehicles|auto|motors?|dealership|dealer)\b/.test(t)) type = 'car';else if (/\b(real estate|property|properties|villa|apartment|agency|broker)\b/.test(t)) type = 'realestate';else if (/\b(recruit|hiring|hire|jobs?|candidates?|talent)\b/.test(t)) type = 'recruitment';else if (/\b(service|services|provider|cleaning|moving|maintenance)\b/.test(t)) type = 'service';else if (/\b(products?|furniture|electronics|goods|items?|inventory|shop|store)\b/.test(t)) type = 'product';
  // Enterprise signals: dealer / agency / recruiter / company / multiple
  const enterprise = /\b(dealer|dealership|agency|recruiter|recruitment|company|enterprise|fleet|represent)\b/.test(t);
  // Business signals: ongoing selling, inventory, multiple, manage
  const business = /\b(business|shop|store|inventory|stock|products?|services?|multiple|many|several|bulk|manage (leads|inquiries|listings))\b/.test(t);
  // Individual: "sell my <single item>"
  const individual = /\bsell(ing)?\s+(my|a|an|this|one)\s+\w+/.test(t);
  let tier: SellIntent['tier'] = 'individual';
  if (enterprise) tier = 'enterprise';else if (business) tier = 'business';else if (individual) tier = 'individual';else tier = 'business'; // generic seller phrasing leans business
  return {
    tier,
    type
  };
};
const BUSINESS_LABEL_INLINE: Record<BusinessType, string> = {
  car: 'vehicle inventory',
  realestate: 'property listings',
  recruitment: 'job postings and candidates',
  service: 'services',
  product: 'product inventory',
  generic: 'listings'
};
const suggestedPrompts = [{
  text: 'Find me a Prado under AED 90,000',
  icon: CarIcon
}, {
  text: "Need a villa near my children's school",
  icon: HomeIcon
}, {
  text: 'Help me find a job',
  icon: BriefcaseIcon
}, {
  text: 'Find a moving company tomorrow',
  icon: WrenchIcon
}, {
  text: 'Compare iPhone 16 Pro prices',
  icon: SmartphoneIcon
}, {
  text: 'Find flights to Istanbul',
  icon: PlaneIcon
}];
const categoryRibbonItems = [{
  label: 'Cars',
  icon: CarIcon,
  prompt: 'Find me a car'
}, {
  label: 'Properties',
  icon: HomeIcon,
  prompt: 'Find me a property'
}, {
  label: 'Jobs',
  icon: BriefcaseIcon,
  prompt: 'Help me find a job'
}, {
  label: 'Services',
  icon: WrenchIcon,
  prompt: 'I need a service'
}, {
  label: 'Products',
  icon: ShoppingBagIcon,
  prompt: "I'm looking for a product"
}, {
  label: 'Moving',
  icon: TruckIcon,
  prompt: 'Find me a moving company'
}, {
  label: 'Education',
  icon: GraduationCapIcon,
  prompt: 'Find education or courses'
}, {
  label: 'Travel',
  icon: PlaneIcon,
  prompt: 'Plan my travel'
}, {
  label: 'Professionals',
  icon: UserIcon,
  prompt: 'Find a professional'
}];
const recentSearches = ['Prado under AED 90,000', 'Villa near GEMS School', 'ERP Manager job in Dubai', 'iPhone 16 Pro price'];
// Mock data for search results
const mockInsights = [{
  title: 'Average Price',
  value: 'AED 84,000',
  type: 'neutral' as const,
  icon: TrendingDownIcon
}, {
  title: 'Best Value',
  value: 'Prado GXR 2018',
  type: 'positive' as const,
  icon: ThumbsUpIcon,
  targetId: '1'
}, {
  title: 'Lowest Mileage',
  value: 'Prado VX 2019',
  type: 'positive' as const,
  icon: SparklesIcon,
  targetId: '4'
}, {
  title: 'Risk Alerts',
  value: '4 imported detected',
  type: 'warning' as const,
  icon: AlertTriangleIcon
}];
const propertyInsights = [{
  title: 'Average Price',
  value: 'AED 2.8M',
  type: 'neutral' as const,
  icon: TrendingDownIcon
}, {
  title: 'Best Value',
  value: '5BR Villa — Mudon',
  type: 'positive' as const,
  icon: ThumbsUpIcon,
  targetId: 'p3'
}, {
  title: 'Closest to School',
  value: 'Dubai Hills · 3 min',
  type: 'positive' as const,
  icon: HomeIcon,
  targetId: 'p5'
}, {
  title: 'Note',
  value: '2 listings under offer',
  type: 'warning' as const,
  icon: AlertTriangleIcon
}];
type SearchCategory = 'cars' | 'property';
const detectCategory = (text: string): SearchCategory => {
  const t = text.toLowerCase();
  const propertyHints = ['villa', 'apartment', 'apt', 'home', 'house', 'property', 'bedroom', 'br ', 'townhouse', 'flat', 'rent', 'school', 'studio'];
  return propertyHints.some(h => t.includes(h)) ? 'property' : 'cars';
};
const CAR_IMG_A = 'https://images.unsplash.com/photo-1590362891991-f776e747a588?auto=format&fit=crop&q=80&w=800';
const CAR_IMG_B = 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&q=80&w=800';
const CAR_IMG_C = 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?auto=format&fit=crop&q=80&w=800';
// Full result set (label says 27; we show a representative slice in the carousel)
const mockListings = [{
  id: '1',
  title: 'Toyota Prado GXR 2018',
  price: 'AED 82,000',
  location: 'Al Quoz, Dubai',
  images: [CAR_IMG_A, CAR_IMG_B],
  specs: [{
    label: 'Year',
    value: '2018'
  }, {
    label: 'Mileage',
    value: '112,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'White'
  }],
  matchScore: 98,
  sellerType: 'Verified Business' as const
}, {
  id: '2',
  title: 'Toyota Prado TXL 2019',
  price: 'AED 89,000',
  location: 'Abu Shagara, Sharjah',
  images: [CAR_IMG_B, CAR_IMG_A],
  specs: [{
    label: 'Year',
    value: '2019'
  }, {
    label: 'Mileage',
    value: '85,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'Silver'
  }],
  matchScore: 94,
  sellerType: 'Individual' as const
}, {
  id: '3',
  title: 'Toyota Prado VXR 2017',
  price: 'AED 78,000',
  location: 'Motor City, Dubai',
  images: [CAR_IMG_C, CAR_IMG_A],
  specs: [{
    label: 'Year',
    value: '2017'
  }, {
    label: 'Mileage',
    value: '145,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'Black'
  }],
  matchScore: 88,
  sellerType: 'Dealer' as const
}, {
  id: '4',
  title: 'Toyota Prado VX 2019',
  price: 'AED 87,500',
  location: 'Mussafah, Abu Dhabi',
  images: [CAR_IMG_A, CAR_IMG_C],
  specs: [{
    label: 'Year',
    value: '2019'
  }, {
    label: 'Mileage',
    value: '64,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'White'
  }],
  matchScore: 92,
  sellerType: 'Verified Business' as const
}, {
  id: '5',
  title: 'Toyota Prado GXR 2016',
  price: 'AED 69,000',
  location: 'Al Nahda, Sharjah',
  images: [CAR_IMG_B, CAR_IMG_C],
  specs: [{
    label: 'Year',
    value: '2016'
  }, {
    label: 'Mileage',
    value: '168,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'Grey'
  }],
  matchScore: 81,
  sellerType: 'Individual' as const
}, {
  id: '6',
  title: 'Toyota Prado TXL 2018',
  price: 'AED 84,900',
  location: 'Deira, Dubai',
  images: [CAR_IMG_C, CAR_IMG_B],
  specs: [{
    label: 'Year',
    value: '2018'
  }, {
    label: 'Mileage',
    value: '98,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'White'
  }],
  matchScore: 90,
  sellerType: 'Dealer' as const
}, {
  id: '7',
  title: 'Toyota Prado VX 2018',
  price: 'AED 86,000',
  location: 'JVC, Dubai',
  images: [CAR_IMG_A, CAR_IMG_B],
  specs: [{
    label: 'Year',
    value: '2018'
  }, {
    label: 'Mileage',
    value: '77,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'Beige'
  }],
  matchScore: 89,
  sellerType: 'Verified Business' as const
}, {
  id: '8',
  title: 'Toyota Prado GXR 2017',
  price: 'AED 74,500',
  location: 'Al Ain',
  images: [CAR_IMG_B, CAR_IMG_C],
  specs: [{
    label: 'Year',
    value: '2017'
  }, {
    label: 'Mileage',
    value: '132,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'Silver'
  }],
  matchScore: 83,
  sellerType: 'Individual' as const
}];
// The 3 strong matches after the user narrows their request
const narrowedListings = [mockListings[3], mockListings[6], mockListings[0]];
// ---- Real-estate (property) dataset ----
const HOME_IMG_A = 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&q=80&w=800';
const HOME_IMG_B = 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&q=80&w=800';
const HOME_IMG_C = 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&q=80&w=800';
const propertyListings: ListingCardProps[] = [{
  id: 'p1',
  title: '4BR Villa — Arabian Ranches',
  price: 'AED 3.2M',
  location: 'Arabian Ranches, Dubai',
  images: [HOME_IMG_A, HOME_IMG_C],
  specs: [{
    label: 'Bedrooms',
    value: '4 BR'
  }, {
    label: 'Bathrooms',
    value: '5 Bath'
  }, {
    label: 'Area',
    value: '3,400 sqft'
  }, {
    label: 'School',
    value: '5 min'
  }],
  matchScore: 97,
  sellerType: 'Verified Business' as const
}, {
  id: 'p2',
  title: '3BR Townhouse — Town Square',
  price: 'AED 1.9M',
  location: 'Town Square, Dubai',
  images: [HOME_IMG_B, HOME_IMG_A],
  specs: [{
    label: 'Bedrooms',
    value: '3 BR'
  }, {
    label: 'Bathrooms',
    value: '4 Bath'
  }, {
    label: 'Area',
    value: '2,100 sqft'
  }, {
    label: 'School',
    value: '8 min'
  }],
  matchScore: 93,
  sellerType: 'Dealer' as const
}, {
  id: 'p3',
  title: '5BR Villa — Mudon',
  price: 'AED 4.1M',
  location: 'Mudon, Dubai',
  images: [HOME_IMG_C, HOME_IMG_B],
  specs: [{
    label: 'Bedrooms',
    value: '5 BR'
  }, {
    label: 'Bathrooms',
    value: '6 Bath'
  }, {
    label: 'Area',
    value: '4,200 sqft'
  }, {
    label: 'School',
    value: '4 min'
  }],
  matchScore: 95,
  sellerType: 'Verified Business' as const
}, {
  id: 'p4',
  title: '4BR Villa — The Springs',
  price: 'AED 2.8M',
  location: 'The Springs, Dubai',
  images: [HOME_IMG_A, HOME_IMG_B],
  specs: [{
    label: 'Bedrooms',
    value: '4 BR'
  }, {
    label: 'Bathrooms',
    value: '4 Bath'
  }, {
    label: 'Area',
    value: '2,900 sqft'
  }, {
    label: 'School',
    value: '6 min'
  }],
  matchScore: 91,
  sellerType: 'Individual' as const
}, {
  id: 'p5',
  title: '3BR Apartment — Dubai Hills',
  price: 'AED 2.3M',
  location: 'Dubai Hills Estate',
  images: [HOME_IMG_C, HOME_IMG_A],
  specs: [{
    label: 'Bedrooms',
    value: '3 BR'
  }, {
    label: 'Bathrooms',
    value: '3 Bath'
  }, {
    label: 'Area',
    value: '1,800 sqft'
  }, {
    label: 'School',
    value: '3 min'
  }],
  matchScore: 89,
  sellerType: 'Dealer' as const
}, {
  id: 'p6',
  title: '4BR Villa — Reem Community',
  price: 'AED 2.6M',
  location: 'Reem, Dubai',
  images: [HOME_IMG_B, HOME_IMG_C],
  specs: [{
    label: 'Bedrooms',
    value: '4 BR'
  }, {
    label: 'Bathrooms',
    value: '5 Bath'
  }, {
    label: 'Area',
    value: '3,100 sqft'
  }, {
    label: 'School',
    value: '7 min'
  }],
  matchScore: 88,
  sellerType: 'Verified Business' as const
}];
const propertyNarrowed = [propertyListings[2], propertyListings[0], propertyListings[4]];
type SelectedVehicle = {
  id: string;
  title: string;
};
type ChatMessage = {
  role: 'user' | 'ai';
  content: string;
  // 'search' = first broad result set (carousel)
  // 'narrow_prompt' = follow-up offer to refine the results
  // 'narrowed' = refined 3-result set
  // 'vehicle_answer' = AI answer about specific selected vehicles
  kind?: 'search' | 'narrow_prompt' | 'narrowed' | 'vehicle_answer' | 'seller_offer' | 'seller_simple';
  vehicles?: SelectedVehicle[];
  category?: SearchCategory;
  sellType?: BusinessType;
};
export function Discover() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    activateHub,
    hub
  } = useBusiness();
  const isShopMode = !!slug;
  const shopLogo = isShopMode && hub?.shopSlug === slug ? hub.logo : null;
  const shopName = isShopMode && hub?.shopSlug === slug ? hub.name : null;
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [hasResults, setHasResults] = useState(false);
  const [hasNarrowed, setHasNarrowed] = useState(false);
  const [searchCategory, setSearchCategory] = useState<SearchCategory>('cars');
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [selectedVehicles, setSelectedVehicles] = useState<SelectedVehicle[]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [attachments, setAttachments] = useState<string[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [showSelectedSheet, setShowSelectedSheet] = useState(false);
  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const names = Array.from(files).map(f => f.name);
    setAttachments(prev => [...prev, ...names]);
    e.target.value = '';
  };
  const removeAttachment = (idx: number) => setAttachments(prev => prev.filter((_, i) => i !== idx));
  const toggleRecording = () => setIsRecording(r => !r);
  // Keep the latest message + input in view as the conversation grows.
  // Scroll the CONTAINER to its full height (more reliable than scrollIntoView
  // when AI replies add tall content/images that grow height after paint).
  useEffect(() => {
    const scrollToBottom = () => {
      const el = scrollContainerRef.current;
      if (el) el.scrollTo({
        top: el.scrollHeight,
        behavior: 'smooth'
      });
    };
    const raf = requestAnimationFrame(scrollToBottom);
    const t1 = setTimeout(scrollToBottom, 250);
    const t2 = setTimeout(scrollToBottom, 700);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [chatHistory, isSearching]);
  const scrollToListing = (id: string) => {
    const el = document.getElementById(`listing-${id}`);
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest'
      });
      el.classList.add('ring-2', 'ring-primary-indigo', 'ring-offset-2');
      setTimeout(() => {
        el.classList.remove('ring-2', 'ring-primary-indigo', 'ring-offset-2');
      }, 1600);
    }
  };
  const resetConversation = () => {
    setChatHistory([]);
    setHasResults(false);
    setHasNarrowed(false);
    setSelectedVehicles([]);
    setSearchCategory('cars');
    setQuery('');
    setIsSearching(false);
  };
  // Clicking "Discover" in the nav navigates to "/" with a fresh `reset`
  // timestamp — start a brand-new chat even if already on this route.
  useEffect(() => {
    if (location.state && (location.state as {
      reset?: number;
    }).reset) {
      resetConversation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);
  // "Activate Business Hub" from the menu — mirror the web portal by sending a
  // selling message so the AI responds with the Business Hub offer.
  const lastSellRef = useRef<number | null>(null);
  useEffect(() => {
    const s = (location.state as {
      sell?: number;
    } | null)?.sell;
    if (s && lastSellRef.current !== s) {
      lastSellRef.current = s;
      resetConversation();
      setTimeout(() => runQuery('I want to sell my products and manage my listings'), 80);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);
  // Business seller accepted — create the workspace and open it
  const createBusinessHub = (type: BusinessType) => {
    // Activate the hub only — this makes the Business Hub nav item appear
    // and pulse, and triggers the global welcome modal. Navigation to
    // /business happens when the user clicks "Get started" in that modal.
    activateHub(type);
  };
  // Business seller declined — fall back to a single listing in chat
  const continueWithOneListing = () => {
    setChatHistory(prev => [...prev, {
      role: 'ai',
      kind: 'seller_simple',
      content: "No problem — let's create a single listing right here. Upload a few photos and tell me the key details, and I'll generate a title, description, and a suggested price for you to review before publishing."
    }]);
  };
  const toggleVehicle = (id: string, title: string) => {
    setSelectedVehicles(prev => prev.some(v => v.id === id) ? prev.filter(v => v.id !== id) : [...prev, {
      id,
      title
    }]);
  };
  // Attach selection state + handler to a listing before rendering
  const withSelection = (listing: ListingCardProps): ListingCardProps => ({
    ...listing,
    selected: selectedVehicles.some(v => v.id === listing.id),
    onToggleSelect: () => toggleVehicle(listing.id, listing.title)
  });
  const runQuery = (text: string) => {
    if (!text.trim() && selectedVehicles.length === 0) return;
    // If vehicles are selected, this is a specific question about them
    if (selectedVehicles.length > 0) {
      const vehicles = selectedVehicles;
      setChatHistory(prev => [...prev, {
        role: 'user',
        content: text || 'Tell me more about these',
        vehicles
      }]);
      setQuery('');
      setSelectedVehicles([]);
      setIsSearching(true);
      setTimeout(() => {
        setIsSearching(false);
        const names = vehicles.map(v => v.title).join(' and ');
        const answer = vehicles.length > 1 ? `Comparing ${names}: both are GCC-spec and well-maintained. The newer one holds resale value better and has lower mileage, while the other is more affordable upfront. For long-term ownership I'd lean toward the lower-mileage option; for budget, the cheaper one is the smarter buy.` : `About the ${names}: it's a GCC-spec unit with a clean history and fair pricing versus the market. Service records check out and the mileage is reasonable for the year. It's a solid, low-risk choice — happy to dig into anything specific like financing, inspection, or negotiation room.`;
        setChatHistory(prev => [...prev, {
          role: 'ai',
          kind: 'vehicle_answer',
          content: answer,
          vehicles
        }]);
      }, 1400);
      return;
    }
    // Detect seller intent — route to a one-time listing or a Business Hub offer
    const sellIntent = detectSellIntent(text);
    if (sellIntent) {
      setChatHistory(prev => [...prev, {
        role: 'user',
        content: text
      }]);
      setQuery('');
      setIsSearching(true);
      setTimeout(() => {
        setIsSearching(false);
        if (sellIntent.tier === 'individual') {
          // One-time seller — stay in chat, create a simple listing
          setChatHistory(prev => [...prev, {
            role: 'ai',
            kind: 'seller_simple',
            content: "Great — upload a few photos and I'll help create the listing. I'll just need a few details: make/model, year, mileage, condition, accident history, GCC specs, and your asking price. Then I'll generate a title, description, and a suggested price for you to review."
          }]);
        } else {
          // Business / enterprise — offer to create a Business Hub
          const intro = sellIntent.tier === 'enterprise' ? `It sounds like you operate a business with multiple listings and customer inquiries. I recommend creating a Business Hub to manage your ${BUSINESS_LABEL_INLINE[sellIntent.type]}, receive leads, track inquiries, manage team access, and use an AI sales assistant.` : `It sounds like you're managing multiple ${BUSINESS_LABEL_INLINE[sellIntent.type]}. I can create a Business Hub for you to manage inventory, leads, messages, and analytics in one place. Would you like me to set it up?`;
          setChatHistory(prev => [...prev, {
            role: 'ai',
            kind: 'seller_offer',
            content: intro,
            sellType: sellIntent.type
          }]);
        }
      }, 1400);
      return;
    }
    const firstSearch = !hasResults;
    const category = firstSearch ? detectCategory(text) : searchCategory;
    if (firstSearch) setSearchCategory(category);
    setChatHistory(prev => [...prev, {
      role: 'user',
      content: text
    }]);
    setQuery('');
    setIsSearching(true);
    setTimeout(() => {
      setIsSearching(false);
      if (firstSearch) {
        setHasResults(true);
        const searchContent = category === 'property' ? 'I found 18 properties that match. I prioritized location, proximity to schools, community quality, and trusted listings.' : 'I found 27 matching vehicles. I prioritized GCC specs, clean history, lower mileage, and trusted sellers.';
        setChatHistory(prev => [...prev, {
          role: 'ai',
          kind: 'search',
          content: searchContent,
          category
        }]);
        // Follow-up narrowing offer ~1s later so the user notices it
        const narrowContent = category === 'property' ? "I can narrow this down — tell me your budget, preferred area, number of bedrooms, or which school matters most, and I'll refine these." : "I can narrow this down for you — just tell me your budget, preferred color, or any other detail, and I'll refine these matches.";
        setTimeout(() => {
          setChatHistory(prev => [...prev, {
            role: 'ai',
            kind: 'narrow_prompt',
            content: narrowContent
          }]);
        }, 1000);
      } else {
        setHasNarrowed(true);
        setChatHistory(prev => [...prev, {
          role: 'ai',
          kind: 'narrowed',
          category,
          content: "Perfect — based on what you told me, I've narrowed it down to just 3 strong matches that fit your preferences best."
        }]);
      }
    }, 1500);
  };
  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    // Fold any attachments into the query context, then clear them
    const attachmentNote = attachments.length > 0 ? ` [Attached: ${attachments.join(', ')}]` : '';
    runQuery(`${query}${attachmentNote}`.trim());
    setAttachments([]);
    setIsRecording(false);
  };
  const handlePromptClick = (prompt: string) => {
    runQuery(prompt);
  };
  return <div className="flex-1 flex flex-col h-full bg-white relative overflow-hidden" data-id="element-2386">
      {/* Hidden file pickers for attachments */}
      <input ref={imageInputRef} type="file" accept="image/*" multiple onChange={handleFilesSelected} className="hidden" data-id="element-2387" />
      <input ref={docInputRef} type="file" accept="image/*,.pdf,.doc,.docx,.csv,.xlsx,.txt" multiple onChange={handleFilesSelected} className="hidden" data-id="element-2388" />
      {/* Background Illustrations — premium, calm, mobile-native */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0 bg-slate-50/50" data-id="element-2389">
        <div className="absolute top-0 left-0 w-full h-[40vh] bg-gradient-to-b from-primary-indigo/[0.03] to-transparent" data-id="element-2390" />
        <motion.div animate={{
        opacity: [0.3, 0.5, 0.3]
      }} transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute top-[-10%] right-[-10%] w-[60vw] h-[60vw] bg-ai-purple/[0.04] blur-[80px] rounded-full" data-id="element-2391" />
        <motion.div animate={{
        opacity: [0.2, 0.4, 0.2]
      }} transition={{
        duration: 10,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 2
      }} className="absolute top-[20%] left-[-20%] w-[70vw] h-[70vw] bg-primary-indigo/[0.03] blur-[100px] rounded-full" data-id="element-2392" />
      </div>

      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-4 pb-24 pt-2 relative z-10" data-id="element-2393">
        <div className="max-w-6xl mx-auto w-full h-full flex flex-col" data-id="element-2394">
          {!hasResults && !isSearching && chatHistory.length === 0 ?
        // Premium Mobile App Home State
        <div className="flex-1 flex flex-col pt-8 pb-52" data-id="element-2395">
              <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="w-full flex flex-col relative z-10" data-id="element-2396">
                {/* Greeting */}
                <div className="px-2 mb-8" data-id="element-2397">
                  {isShopMode ? <div className="flex items-center gap-3" data-id="element-2398">
                      {shopLogo ? <img src={shopLogo} alt={shopName || 'Shop'} className="w-10 h-10 rounded-full object-cover shadow-sm border border-slate-100" data-id="element-2399" /> : <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-indigo to-ai-purple flex items-center justify-center shadow-sm" data-id="element-2400">
                          <SparklesIcon className="w-5 h-5 text-white" data-id="element-2401" />
                        </div>}
                      <div data-id="element-2402">
                        <h1 className="font-display font-bold text-2xl text-dark-navy tracking-tight" data-id="element-2403">
                          {shopName || 'Our Shop'}
                        </h1>
                        <p className="text-slate-500 text-sm" data-id="element-2404">
                          How can we help you today?
                        </p>
                      </div>
                    </div> : <div className="flex flex-col gap-1" data-id="element-2405">
                      <div className="flex items-center gap-2 mb-1" data-id="element-2406">
                        <SparklesIcon className="w-5 h-5 text-primary-indigo" data-id="element-2407" />
                        <span className="text-sm font-semibold text-primary-indigo uppercase tracking-wider" data-id="element-2408">
                          Talab AI
                        </span>
                      </div>
                      <h1 className="font-display font-bold text-[28px] leading-tight text-dark-navy tracking-tight" data-id="element-2409">
                        Good morning.
                        <br data-id="element-2410" />
                        <span className="text-slate-400 font-medium" data-id="element-2411">
                          What are you looking for?
                        </span>
                      </h1>
                    </div>}
                </div>

                {/* Suggested Prompts */}
                <div className="px-2 mb-4" data-id="element-2412">
                  <div className="flex items-center justify-between mb-3" data-id="element-2413">
                    <h2 className="text-[13px] font-semibold text-slate-500 uppercase tracking-wider" data-id="element-2414">
                      Suggestions
                    </h2>
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-2415">
                    {suggestedPrompts.slice(0, 4).map((prompt, index) => {
                  const Icon = prompt.icon;
                  return <button key={index} onClick={() => handlePromptClick(prompt.text)} className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-100 rounded-full hover:border-slate-200 transition-all text-left shadow-[0_2px_10px_rgb(0,0,0,0.02)]" data-id="element-2416">
                          <Icon className="w-4 h-4 text-primary-indigo/70" data-id="element-2417" />
                          <span className="text-[13px] font-medium text-slate-600" data-id="element-2418">
                            {prompt.text}
                          </span>
                        </button>;
                })}
                  </div>
                </div>
              </motion.div>
            </div> :
        // Chat History State
        <div className="space-y-6 pb-36 pt-2" data-id="element-2419">
              {chatHistory.map((msg, idx) => <div key={idx} data-id="element-2420">
                  {msg.role === 'user' ? <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="flex justify-end mb-6" data-id="element-2421">
                      <div className="bg-slate-100 rounded-2xl rounded-tr-sm px-5 py-3 max-w-[80%] shadow-sm" data-id="element-2422">
                        {msg.vehicles && msg.vehicles.length > 0 && <div className="flex flex-wrap gap-2 mb-3" data-id="element-2423">
                            {msg.vehicles.map(v => <span key={v.id} className="inline-flex items-center gap-1.5 bg-white border border-primary-indigo/20 text-primary-indigo text-xs font-medium px-3 py-1.5 rounded-full" data-id="element-2424">
                                <SparklesIcon className="w-3.5 h-3.5" data-id="element-2425" />
                                {v.title}
                              </span>)}
                          </div>}
                        <p className="text-slate-800 text-[15px]" data-id="element-2426">
                          {msg.content}
                        </p>
                      </div>
                    </motion.div> : <div className="mb-6" data-id="element-2427">
                      <AIResponse message={msg.content} insights={msg.kind === 'search' ? msg.category === 'property' ? propertyInsights : mockInsights : undefined} onInsightClick={scrollToListing} data-id="element-2428" />

                      {msg.kind === 'seller_offer' && <div className="mt-4 space-y-2.5" data-id="element-2429">
                          <button onClick={() => createBusinessHub(msg.sellType ?? 'generic')} className="w-full flex items-center gap-3 p-3.5 bg-primary-indigo text-white rounded-2xl shadow-[0_8px_24px_rgb(67,56,202,0.3)] active:scale-[0.98] transition-transform text-left" data-id="element-2430">
                            <span className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0" data-id="element-2431">
                              <BriefcaseIcon className="w-5 h-5" data-id="element-2432" />
                            </span>
                            <span className="flex-1 min-w-0" data-id="element-2433">
                              <span className="block font-semibold text-[15px] leading-tight" data-id="element-2434">
                                Create Business Hub
                              </span>
                              <span className="block text-[12px] text-white/70 leading-tight" data-id="element-2435">
                                Manage inventory, leads & analytics
                              </span>
                            </span>
                            <ChevronRightIcon className="w-5 h-5 text-white/70 flex-shrink-0" data-id="element-2436" />
                          </button>
                          <button onClick={continueWithOneListing} className="w-full flex items-center justify-center gap-1.5 py-3 bg-white border border-slate-200 text-slate-600 rounded-2xl text-sm font-medium active:bg-slate-50 transition-colors" data-id="element-2437">
                            Continue with one listing
                          </button>
                        </div>}

                      {msg.kind === 'narrowed' ?
              // Narrowed: 3 strong matches
              <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: 0.4
              }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5 ml-0 md:ml-12" data-id="element-2438">
                          {(msg.category === 'property' ? propertyNarrowed : narrowedListings).map((listing, lIdx) => <motion.div key={listing.id} initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.5 + lIdx * 0.1
                }} data-id="element-2439">
                              <ListingCard {...withSelection(listing)} data-id="element-2440" />
                            </motion.div>)}
                        </motion.div> : msg.kind === 'search' ?
              // Broad search: swipeable carousel
              <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: 0.4
              }} className="mt-6" data-id="element-2441">
                          <div className="flex items-center justify-between mb-3" data-id="element-2442">
                            <span className="text-sm font-medium text-slate-500" data-id="element-2443">
                              {msg.category === 'property' ? 'Showing top matches of 18' : 'Showing top matches of 27'}
                            </span>
                          </div>
                          <div className="-mx-4" data-id="element-2444">
                            <ListingCarousel listings={(msg.category === 'property' ? propertyListings : mockListings).map(withSelection)} data-id="element-2445" />
                          </div>
                        </motion.div> : null}
                    </div>}
                </div>)}

              {isSearching && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="flex gap-4" data-id="element-2446">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-primary-indigo to-ai-purple flex items-center justify-center shadow-md" data-id="element-2447">
                    <SparklesIcon className="w-5 h-5 text-white animate-pulse" data-id="element-2448" />
                  </div>
                  <div className="bg-white rounded-2xl rounded-tl-none p-5 shadow-soft border border-slate-100 flex items-center gap-2" data-id="element-2449">
                    <div className="w-2 h-2 bg-primary-indigo rounded-full animate-bounce" style={{
                animationDelay: '0ms'
              }} data-id="element-2450" />
                    <div className="w-2 h-2 bg-primary-indigo rounded-full animate-bounce" style={{
                animationDelay: '150ms'
              }} data-id="element-2451" />
                    <div className="w-2 h-2 bg-primary-indigo rounded-full animate-bounce" style={{
                animationDelay: '300ms'
              }} data-id="element-2452" />
                  </div>
                </motion.div>}
              {/* Scroll anchor — keeps newest message + input in view */}
              <div ref={bottomRef} className="h-px" data-id="element-2453" />
            </div>}
        </div>
      </div>

      {/* Floating Input (always visible while chatting) */}
      {(hasResults || isSearching || chatHistory.length > 0) && <>
          {/* Soft fade so content dissolves behind the floating pill */}
          <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-white via-white/95 to-transparent pointer-events-none z-20" data-id="element-2454" />
          <div className="absolute bottom-6 left-0 right-0 px-4 z-30 pointer-events-none" data-id="element-2455">
            <div className="w-full pointer-events-auto" data-id="element-2456">
              {/* Selected vehicles — grouped counter pill (tap to manage) */}
              {selectedVehicles.length > 0 && <motion.button initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} onClick={() => setShowSelectedSheet(true)} className="mb-3 ml-1 inline-flex items-center gap-2 pl-1.5 pr-3 py-1.5 bg-white border border-primary-indigo/20 rounded-full shadow-[0_4px_16px_rgb(0,0,0,0.06)] active:scale-[0.98] transition-transform" data-id="element-2457">
                  <span className="w-6 h-6 rounded-full bg-gradient-to-br from-primary-indigo to-ai-purple text-white text-[11px] font-bold flex items-center justify-center shadow-sm" data-id="element-2458">
                    {selectedVehicles.length}
                  </span>
                  <span className="text-[13px] font-medium text-slate-700" data-id="element-2459">
                    Asking about{' '}
                    {selectedVehicles.length > 1 ? `${selectedVehicles.length} items` : '1 item'}
                  </span>
                  <ChevronUpIcon className="w-4 h-4 text-slate-400" data-id="element-2460" />
                </motion.button>}
              {/* Attachment chips */}
              {attachments.length > 0 && <div className="flex flex-wrap items-center gap-2 mb-3 px-1" data-id="element-2461">
                  {attachments.map((name, i) => <span key={i} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 text-xs font-medium pl-3 pr-1.5 py-1.5 rounded-full shadow-sm" data-id="element-2462">
                      <PaperclipIcon className="w-3.5 h-3.5" data-id="element-2463" />
                      <span className="max-w-[140px] truncate" data-id="element-2464">{name}</span>
                      <button onClick={() => removeAttachment(i)} className="p-0.5 hover:bg-slate-100 rounded-full" aria-label={`Remove ${name}`} data-id="element-2465">
                        <XIcon className="w-3.5 h-3.5" data-id="element-2466" />
                      </button>
                    </span>)}
                </div>}
              <div className="w-full bg-white rounded-[28px] shadow-[0_12px_40px_rgb(0,0,0,0.12)] border border-slate-100 p-1.5 relative group" data-id="element-2467">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-primary-indigo/20 to-ai-purple/20 rounded-[30px] blur opacity-0 group-focus-within:opacity-100 transition duration-500" data-id="element-2468" />
                <div className="relative bg-white rounded-[24px] flex items-end gap-1.5 px-3 py-1.5" data-id="element-2469">
                  <button type="button" onClick={() => docInputRef.current?.click()} aria-label="Attach a file or image" className="p-2 text-slate-400 hover:text-primary-indigo hover:bg-slate-50 rounded-full transition-colors flex-shrink-0 mb-0.5" data-id="element-2470">
                    <PaperclipIcon className="w-5 h-5" data-id="element-2471" />
                  </button>
                  <textarea value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }} rows={1} placeholder={selectedVehicles.length > 0 ? `Ask about ${selectedVehicles.length > 1 ? 'these vehicles' : 'this vehicle'}...` : 'Ask Talab anything...'} className="flex-1 bg-transparent outline-none text-[15px] text-slate-800 placeholder:text-slate-400 resize-none py-1.5 leading-relaxed overflow-hidden" data-id="element-2472" />
                  <button type="button" onClick={toggleRecording} aria-label="Voice input" className={`p-2 rounded-full transition-colors flex-shrink-0 mb-0.5 ${isRecording ? 'text-white bg-error animate-pulse' : 'text-slate-400 hover:text-primary-indigo hover:bg-slate-50'}`} data-id="element-2473">
                    <MicIcon className="w-5 h-5" data-id="element-2474" />
                  </button>
                  <button onClick={() => handleSubmit()} disabled={!query.trim() && selectedVehicles.length === 0 && attachments.length === 0 || isSearching} className="w-10 h-10 bg-dark-navy text-white rounded-full flex items-center justify-center hover:bg-slate-800 transition-colors disabled:opacity-30 disabled:bg-slate-200 disabled:text-slate-400 shadow-sm flex-shrink-0 mb-0.5" data-id="element-2475">
                    <ArrowUpIcon className="w-5 h-5" data-id="element-2476" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* Selected items bottom sheet */}
      {createPortal(<AnimatePresence data-id="element-2477">
          {showSelectedSheet && selectedVehicles.length > 0 && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-[110] flex flex-col justify-end" data-id="element-2478">
              <div className="absolute inset-0 bg-dark-navy/40 backdrop-blur-sm" onClick={() => setShowSelectedSheet(false)} data-id="element-2479" />
              <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 320
        }} className="relative bg-white rounded-t-3xl shadow-2xl flex flex-col max-h-[70%] pb-7" role="dialog" aria-modal="true" aria-label="Selected items" data-id="element-2480">
                {/* Grabber */}
                <div className="pt-3 flex justify-center" data-id="element-2481">
                  <div className="w-10 h-1.5 rounded-full bg-slate-200" data-id="element-2482" />
                </div>
                {/* Header */}
                <div className="px-5 pt-3 pb-3 flex items-center justify-between border-b border-slate-100" data-id="element-2483">
                  <div className="flex items-center gap-2" data-id="element-2484">
                    <h3 className="font-display font-bold text-[17px] text-dark-navy" data-id="element-2485">
                      Asking about
                    </h3>
                    <span className="w-6 h-6 rounded-full bg-primary-indigo/10 text-primary-indigo text-[12px] font-bold flex items-center justify-center" data-id="element-2486">
                      {selectedVehicles.length}
                    </span>
                  </div>
                  <button onClick={() => setShowSelectedSheet(false)} aria-label="Close" className="p-2 -mr-1 rounded-full hover:bg-slate-100 transition-colors" data-id="element-2487">
                    <XIcon className="w-5 h-5 text-slate-500" data-id="element-2488" />
                  </button>
                </div>
                {/* List */}
                <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1.5" data-id="element-2489">
                  {selectedVehicles.map(v => <div key={v.id} className="flex items-center gap-3 px-3 py-3 rounded-2xl bg-slate-50" data-id="element-2490">
                      <div className="w-9 h-9 rounded-xl bg-white border border-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-2491">
                        <SparklesIcon className="w-4 h-4 text-primary-indigo" data-id="element-2492" />
                      </div>
                      <span className="flex-1 min-w-0 text-[14px] font-medium text-slate-800 truncate" data-id="element-2493">
                        {v.title}
                      </span>
                      <button onClick={() => {
                toggleVehicle(v.id, v.title);
                if (selectedVehicles.length <= 1) setShowSelectedSheet(false);
              }} aria-label={`Remove ${v.title}`} className="p-1.5 rounded-full hover:bg-slate-200 transition-colors flex-shrink-0" data-id="element-2494">
                        <XIcon className="w-4 h-4 text-slate-500" data-id="element-2495" />
                      </button>
                    </div>)}
                </div>
                {/* Footer */}
                <div className="px-5 pt-2" data-id="element-2496">
                  <button onClick={() => {
              setSelectedVehicles([]);
              setShowSelectedSheet(false);
            }} className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-slate-100 text-slate-700 text-[14px] font-semibold hover:bg-slate-200 transition-colors" data-id="element-2497">
                    <Trash2Icon className="w-4 h-4" data-id="element-2498" />
                    Clear all
                  </button>
                </div>
              </motion.div>
            </motion.div>}
        </AnimatePresence>, typeof document !== 'undefined' && document.getElementById('app-screen-root') || document.body)}

      {/* Floating premium composer — Discover home (no bottom nav here) */}
      {!hasResults && !isSearching && chatHistory.length === 0 && <>
          {/* Soft fade so content dissolves behind the floating composer */}
          <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-white via-white/95 to-transparent pointer-events-none z-20" data-id="element-2499" />
          <div className="absolute bottom-6 left-0 right-0 px-4 z-30 pointer-events-none" data-id="element-2500">
            <div className="w-full pointer-events-auto" data-id="element-2501">
              {/* Attachment chips */}
              {attachments.length > 0 && <div className="flex flex-wrap items-center gap-2 mb-3 px-1" data-id="element-2502">
                  {attachments.map((name, i) => <span key={i} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 text-xs font-medium pl-3 pr-1.5 py-1.5 rounded-full shadow-sm" data-id="element-2503">
                      <PaperclipIcon className="w-3.5 h-3.5" data-id="element-2504" />
                      <span className="max-w-[140px] truncate" data-id="element-2505">{name}</span>
                      <button onClick={() => removeAttachment(i)} className="p-0.5 hover:bg-slate-100 rounded-full" aria-label={`Remove ${name}`} data-id="element-2506">
                        <XIcon className="w-3.5 h-3.5" data-id="element-2507" />
                      </button>
                    </span>)}
                </div>}

              <motion.div initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="w-full bg-white rounded-[28px] shadow-[0_12px_40px_rgb(0,0,0,0.12)] border border-slate-100 p-1.5 relative group" data-id="element-2508">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-primary-indigo/20 to-ai-purple/20 rounded-[30px] blur opacity-0 group-focus-within:opacity-100 transition duration-500" data-id="element-2509" />
                <div className="relative bg-white rounded-[24px] flex items-end gap-1.5 px-3 py-1.5" data-id="element-2510">
                  <button type="button" onClick={() => docInputRef.current?.click()} aria-label="Attach a file or image" className="p-2 text-slate-400 hover:text-primary-indigo hover:bg-slate-50 rounded-full transition-colors flex-shrink-0 mb-0.5" data-id="element-2511">
                    <PaperclipIcon className="w-5 h-5" data-id="element-2512" />
                  </button>
                  <textarea ref={textareaRef} value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }} rows={1} placeholder={isShopMode ? `Ask ${shopName || 'us'} anything...` : 'Ask Talab anything...'} className="flex-1 bg-transparent outline-none text-[15px] text-slate-800 placeholder:text-slate-400 resize-none py-1.5 leading-relaxed overflow-hidden" data-id="element-2513" />
                  <button type="button" onClick={toggleRecording} aria-label="Voice input" className={`p-2 rounded-full transition-colors flex-shrink-0 mb-0.5 ${isRecording ? 'text-white bg-error animate-pulse' : 'text-slate-400 hover:text-primary-indigo hover:bg-slate-50'}`} data-id="element-2514">
                    <MicIcon className="w-5 h-5" data-id="element-2515" />
                  </button>
                  <button onClick={() => handleSubmit()} disabled={!query.trim() && attachments.length === 0} className="w-10 h-10 bg-dark-navy text-white rounded-full flex items-center justify-center hover:bg-slate-800 transition-colors disabled:opacity-30 disabled:bg-slate-200 disabled:text-slate-400 shadow-sm flex-shrink-0 mb-0.5" data-id="element-2516">
                    <ArrowUpIcon className="w-5 h-5" data-id="element-2517" />
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </>}
    </div>;
}